import styled from 'styled-components';
import { Fonts, Palette } from '../../components/utils';
import { Flex } from '../../components/ui/Flex';
import { Input } from '../../components/ui/Input';

export const DocumentOptionCard = styled(Flex)`
  min-height: 5.5rem;
  padding: 1.25rem 1.125rem;
  align-items: flex-start;
  gap: 1rem;
  border-radius: 0.625rem;
  border: 1px solid ${Palette.DISABLED};
  background: ${Palette.WHITE};
  cursor: ${({ $disabled }) => ($disabled ? 'not-allowed' : 'pointer')};
  opacity: ${({ $disabled }) => ($disabled ? 0.6 : 1)};
  pointer-events: ${({ $disabled }) => ($disabled ? 'none' : 'auto')};

  ${({ $selected }) =>
    $selected &&
    `
    border: 1px solid ${Palette.HAZY_BLUE};
    background: ${Palette.SOLITUDE};
  `}
`;

export const DocumentOptionTitle = styled.div`
  color: ${Palette.BLACK};
  font-family: ${Fonts.RED_HAT_FONT}, sans-serif;
  font-size: 1rem;
  font-weight: 800;
  line-height: 1.5rem;
`;

export const DocumentOptionDescription = styled.div`
  color: ${Palette.BLACK_50};
  font-family: ${Fonts.OPEN_SANS_FONT}, sans-serif;
  font-size: 0.875rem;
  font-weight: 400;
  line-height: 1.25rem;
  text-align: left;
`;

export const DocumentSectionHeading = styled.div`
  color: ${Palette.BLACK};
  font-family: ${Fonts.RED_HAT_FONT}, sans-serif;
  font-size: 1.25rem;
  font-weight: 700;
  line-height: 1.75rem;
`;

export const DocumentSection = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
  width: 100%;
`;

export const DocumentOptionsList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
  width: 100%;
`;

export const DocumentFilePreview = styled(Flex)`
  padding: 0.625rem 1rem;
  border-radius: 10px;
  border: 1px solid ${Palette.STROKE_GREY};
  font-weight: 600;
  color: ${Palette.BLACK};
  min-width: 0;
  width: 100%;

  p {
    margin-bottom: 0;
  }
`;

export const DocumentFilePreviewInfo = styled.div`
  min-width: 0;
  flex: 1;
`;

export const DocumentFilePreviewName = styled.p`
  margin-bottom: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

export const DocumentFileName = styled.p`
  margin-bottom: 0;
  color: ${Palette.BLACK};
  font-family: ${Fonts.OPEN_SANS_FONT}, sans-serif;
  font-size: 1rem;
  font-weight: 600;
  line-height: 1.5rem;
`;

export const DocumentFileSize = styled.p`
  font-size: 0.75rem;
  color: ${Palette.BLACK_50};
`;

/** Compact type tile used in upload/file preview rows. */
export const DocumentFileTypeIcon = styled.span`
  display: inline-flex;
  flex-shrink: 0;
  align-items: center;
  justify-content: center;
  box-sizing: border-box;
  width: 2.75rem;
  height: 2.5rem;
  padding: 0 0.25rem;
  border-radius: 8px;
  background: ${({ $background }) => $background || Palette.GHOST_WHITE};
  color: ${({ $color }) => $color || Palette.BLACK};
  font-family: ${Fonts.OPEN_SANS_FONT}, sans-serif;
  font-size: 0.625rem;
  font-weight: 700;
  letter-spacing: 0.02em;
  line-height: 1;
  text-transform: uppercase;
`;

export const DocumentUploadStatusText = styled.p`
  margin-bottom: 0;
  font-size: 0.875rem;
  font-weight: 600;
  color: ${({ $stalled }) => ($stalled ? Palette.ERROR : Palette.HAZY_BLUE)};
`;

export const DocumentUploadProgressTrack = styled.div`
  width: 100%;
  height: 6px;
  border-radius: 999px;
  background: ${Palette.STROKE_GREY};
  overflow: hidden;
`;

export const DocumentUploadProgressFill = styled.div`
  height: 100%;
  width: ${({ $progress }) => `${Math.min(100, Math.max(0, $progress * 100))}%`};
  border-radius: 999px;
  background: ${({ $stalled }) => ($stalled ? Palette.ERROR : Palette.HAZY_BLUE)};
  transition: width 0.25s ease;
`;

export const DocumentUploadProgressRow = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
  width: 100%;
`;

export const DocumentFileMetaRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 1rem;
  width: 100%;
`;

export const DocumentFileMetaLabel = styled.span`
  font-family: ${Fonts.OPEN_SANS_FONT}, sans-serif;
  font-size: 0.875rem;
  font-weight: 400;
  line-height: 1.25rem;
  color: ${Palette.BLACK_50};
`;

export const DocumentFileMetaValue = styled.span`
  font-family: ${Fonts.OPEN_SANS_FONT}, sans-serif;
  font-size: 0.875rem;
  font-weight: 400;
  line-height: 1.25rem;
  color: ${Palette.BLACK};
  text-align: right;
`;

export const DocumentClearFileButton = styled.button`
  display: flex;
  padding: 0;
  border: none;
  background-color: transparent;
  cursor: pointer;

  svg {
    width: 1.5rem;
    height: 1.5rem;
    fill: ${Palette.BLACK};
  }
`;

export const DocumentNameInput = styled(Input)`
  border: 1px solid ${Palette.STROKE_GREY};
  border-radius: 10px;
  padding: 0.75rem 1rem;
  min-height: 48px;

  &:focus-visible {
    outline: none;
    border-color: ${Palette.HAZY_BLUE};
  }
`;

export const DocumentDropzoneHint = styled.p`
  font-size: 12px;
  color: ${Palette.CONTENT_TERTIARY};
  margin-bottom: 0;
  margin-top: 8px;
`;

export const DocumentPreviewFrame = styled.div`
  width: 100%;
  border-radius: 10px;
  border: 1px solid ${Palette.STROKE_GREY};
  background: ${Palette.GHOST_WHITE};
  overflow: hidden;
  display: flex;
  align-items: center;
  justify-content: center;
  min-height: 220px;
  max-height: 320px;
`;

export const DocumentPreviewImage = styled.img`
  display: block;
  width: 100%;
  max-height: 320px;
  object-fit: contain;
  background: ${Palette.WHITE};
`;

/** Compact square thumbnail shown beside document details. */
export const DocumentDetailsThumbnail = styled.div`
  flex-shrink: 0;
  width: 80px;
  height: 80px;
  border-radius: 10px;
  border: 1px solid ${Palette.STROKE_GREY};
  background: ${Palette.GHOST_WHITE};
  overflow: hidden;
`;

export const DocumentDetailsThumbnailImage = styled.img`
  display: block;
  width: 100%;
  height: 100%;
  object-fit: cover;
  background: ${Palette.WHITE};
`;

export const DocumentPreviewPdf = styled.iframe`
  display: block;
  width: 100%;
  height: 320px;
  border: none;
  background: ${Palette.GHOST_WHITE};
`;

export const DocumentPreviewVideo = styled.video`
  display: block;
  width: 100%;
  max-height: 320px;
  background: ${Palette.BLACK};
`;

export const DocumentTypePlaceholder = styled.div`
  position: relative;
  width: 100%;
  min-height: 220px;
  border-radius: 10px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: ${({ $background }) => $background || Palette.GHOST_WHITE};
  overflow: hidden;

  ${({ $interactive }) =>
    $interactive &&
    `
    &:hover .document-type-placeholder-open,
    &:focus-within .document-type-placeholder-open {
      opacity: 1;
      max-height: 2.75rem;
      margin-top: 0.75rem;
      padding: 0.5rem 0.875rem;
      pointer-events: auto;
    }
  `}
`;

export const DocumentTypePlaceholderContent = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
`;

export const DocumentTypePlaceholderOpen = styled.button`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 0.375rem;
  margin: 0;
  margin-top: 0;
  padding: 0 0.875rem;
  max-height: 0;
  overflow: hidden;
  border: none;
  border-radius: 8px;
  background: rgba(0, 0, 0, 0.45);
  color: ${Palette.WHITE};
  font-family: ${Fonts.OPEN_SANS_FONT}, sans-serif;
  font-size: 0.875rem;
  font-weight: 600;
  line-height: 1.25rem;
  white-space: nowrap;
  opacity: 0;
  pointer-events: none;
  cursor: pointer;
  transition:
    opacity 0.15s ease,
    max-height 0.15s ease,
    margin-top 0.15s ease,
    padding 0.15s ease;

  svg {
    flex-shrink: 0;
  }
`;

export const DocumentTypeBadge = styled.span`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  padding: 0.25rem 0.625rem;
  border-radius: 6px;
  font-family: ${Fonts.OPEN_SANS_FONT}, sans-serif;
  font-size: 0.75rem;
  font-weight: 700;
  letter-spacing: 0.02em;
  line-height: 1.25rem;
  background: ${({ $background }) => $background || Palette.GHOST_WHITE};
  color: ${({ $color }) => $color || Palette.BLACK};
`;

export const DocumentTypePlaceholderLabel = styled.span`
  font-family: ${Fonts.OPEN_SANS_FONT}, sans-serif;
  font-size: 1.5rem;
  font-weight: 700;
  letter-spacing: 0.04em;
  line-height: 2rem;
  color: ${({ $color }) => $color || Palette.BLACK};
`;
